import {
	EventIdentifier,
	FeatureTeamEvent,
	TrackingEventType,
	useAnalytics,
	useLogger,
	useSettings
} from '@capsicum/common';
import { type GeetestChallengeMetadata, type GeetestSuccessResponse, useGeetest } from '@capsicum/socios-core';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import './styles.scss';
import { GeeTestScript } from './GeeTestScript';

type GeeTestContainerProps = {
	onReady?: () => void;
};

export type GeeTestRef = {
	runCaptcha: (
		onSuccess: (r: GeetestSuccessResponse) => void,
		onErr: (err?: Error) => void,
		metadata?: GeetestChallengeMetadata
	) => void;
};

export const GeeTestContainer = forwardRef<GeeTestRef, GeeTestContainerProps>(function GeeTextContainer(
	{ onReady },
	ref
) {
	const logger = useLogger();
	const analytics = useAnalytics();
	const fetchCaptchaChallenge = useGeetest();
	const container = useRef<HTMLDivElement>(null);
	const [isScriptLoaded, setScriptLoaded] = useState(false);

	const {
		settings: { isGeetestDisabledCapsicum }
	} = useSettings();

	const getCaptchaObject = useCallback<(metadata?: GeetestChallengeMetadata) => Promise<any>>(
		async metadata => {
			try {
				const initGeetest4 = (window as any).initGeetest4;

				if (!isScriptLoaded || isGeetestDisabledCapsicum || !initGeetest4) return;

				const captchaChallenge = await fetchCaptchaChallenge({ metadata });

				if (!captchaChallenge) return;

				return new Promise(resolve => {
					initGeetest4(
						{
							...captchaChallenge,
							hideBar: 'close',
							language: 'eng',
							nextWidth: '100%',
							outside: false,
							product: 'bind',
							protocol: 'https://'
						},
						(obj: any) => {
							if (!container.current) return;
							obj.appendTo(container.current).onReady(() => resolve(obj));
						}
					);
				});
			} catch (e) {
				logger.error('error when fetching captcha object', e as Error);
			}
		},
		[logger, isScriptLoaded, fetchCaptchaChallenge, isGeetestDisabledCapsicum]
	);

	// Provide an method runCaptcha for outside.
	useImperativeHandle(
		ref,
		() => ({
			runCaptcha: (onSuccess, onErr, metadata) => {
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSecurityPuzzleStatus, {
					feature_team: FeatureTeamEvent.OFT,
					status: 'ready'
				});

				if (isGeetestDisabledCapsicum) {
					onSuccess({
						captcha_id: 'string',
						captcha_output: 'string',
						gen_time: 'string',
						lot_number: 'string',
						pass_token: 'string'
					});

					return;
				}

				getCaptchaObject(metadata).then(captchaObj => {
					if (!captchaObj) onErr(new Error('Geetest is not ready '));

					// Listen to success and error of captcha
					captchaObj
						?.onError(onErr)
						.onSuccess(() => {
							const result = captchaObj.getValidate();

							if (result) {
								analytics.sendEvent(
									TrackingEventType.track,
									EventIdentifier.OnboardingSecurityPuzzleStatus,
									{
										feature_team: FeatureTeamEvent.OFT,
										status: 'success'
									}
								);
								onSuccess(result as GeetestSuccessResponse);
							} else {
								onErr();
							}
						})
						.onFail(() => {
							analytics.sendEvent(
								TrackingEventType.track,
								EventIdentifier.OnboardingSecurityPuzzleStatus,
								{
									feature_team: FeatureTeamEvent.OFT,
									status: 'failed'
								}
							);
						});

					// Show the Captcha
					captchaObj.showBox();
				});
			}
		}),
		[isGeetestDisabledCapsicum, analytics, getCaptchaObject]
	);

	// Effects
	useEffect(() => {
		if (isScriptLoaded) onReady?.();
	}, [onReady, isScriptLoaded]);

	return (
		<>
			<div ref={container} />
			<GeeTestScript async onReady={() => setScriptLoaded(true)} />
		</>
	);
});

export default GeeTestContainer;
